import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Studies } from '@app/_assets/studies/studies.model';
import { AlertsService, SettingsService } from '@app/_services';
import { DSVResponse } from '@app/_views/digitalSignView/digitalSignView.model';
import { BehaviorSubject, catchError, forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ClassifiersApi } from '../classifiers/classifiers.api.service';
import { ClassifierAttributeDefinitionCode } from '../classifiers/models/classifier-attribute-definition-code.enum';
import { ClassifierDefinitionCode } from '../classifiers/models/classifier-definition-code.enum';
import { ClassifierItemsQuery } from '../classifiers/models/classifier-items-query';
import { ClassifierItemsQueryItem } from '../classifiers/models/classifier-items-query-item';
import { ClassifierAttribute } from '../classifiers/models/ClassifierAttribute';
import { CertificatesUtility } from './certificates.utility';
import { AccessType } from './models/enums/access-type.enum';
import { GraduationDocumentAttribute } from './models/enums/graduation-document-attribute.enum';
import { GraduationDocumentLanguage } from './models/enums/graduation-document-language.enum';
import { GraduationDocumentType } from './models/enums/graduation-document-type.enum';
import { CertificateResponse } from './models/interfaces/certificate';
import { CertificateAccess, CertificateAccessResponse } from './models/interfaces/certificate-access';
import {
	CertificateAccessDTO,
	CertificateEditAccessDto,
} from './models/interfaces/certificate-access-dto';
import { CertificateAction } from './models/interfaces/certificate-action';
import { CertificateActionResponse } from './models/interfaces/certificate-action-response';
import { CertificateData } from './models/interfaces/certificate-data';
import { CertificateDataIssue, CertificateDataIssueResponse } from './models/interfaces/certificate-data-issue-response';
import {
	CertificateDocumentWithClassifier,
	CertificateStatus,
	FormattedCertificateDocumentData,
} from './models/interfaces/certificate-document';
import { CertificateDocumentResponse } from './models/interfaces/certificate-document-response';
import { CertificateIndex } from './models/interfaces/certificate-index';
import { CertificateTranscriptParams } from './models/interfaces/certificate-transcript-params';
import { CertificatesResponse, GraduationCertificate } from './models/interfaces/graduation-certificate';
import { ApiResponse } from '../trainings/register/trainings-juhan-register.model';
import { CeCertificateFileResponse } from '../my-data/my-training-list/certificate-download/ce-certificates.model';

@Injectable({
	providedIn: 'root',
})
export class CertificatesApi {
	private readonly settings = inject(SettingsService);
	private readonly classifiers = inject(ClassifiersApi);
	private readonly http = inject(HttpClient);
	private readonly alertsService = inject(AlertsService);
	//private readonly certificatesUrl = `${this.settings.ehisUrl}/certificates/v1`;

	/*fetchDocument(documentId: number): Observable<CertificateDocumentResponse> {
	const url = `${this.certificatesUrl}/certificateDocument/${documentId}`;
	return this.http.get<CertificateDocumentResponse>(url);
	}
	fetchDocumentWithParams(
	documentId: number,
	params: {},
	): Observable<CertificateDocumentResponse> {
	const url = `${this.certificatesUrl}/certificateDocument/${documentId}`;
	return this.http.get<CertificateDocumentResponse>(url, { params });
	}*/

	fetchDocument(documentId: number): Observable<CertificateDocumentResponse> {
		const url = `${this.settings.baseUrl}/api/certificateDocument/${documentId}`;

		return this.http
			.get<ApiResponse<CertificateDocumentResponse>>(url)
			.pipe(
				catchError(() => of({statusMessage: { status: 'ERROR' }, response: {}} as ApiResponse<CertificateDocumentResponse>)),
				map((res: ApiResponse<CertificateDocumentResponse>) => res?.response),
			);
	}

	fetchDocumentWithParams(
		documentId: number,
		params: {},
	): Observable<CertificateDocumentResponse> {
		const url = `${this.settings.baseUrl}/api/certificateDocument/${documentId}`;

		return this.http
			.get<ApiResponse<CertificateDocumentResponse>>(url, { params })
			.pipe(
				catchError(() => of({statusMessage: { status: 'ERROR' }, response: {}} as ApiResponse<CertificateDocumentResponse>)),
				map((res: ApiResponse<CertificateDocumentResponse>) => res?.response),
			);
	}

	/*downloadTranscript(
		id: number,
		params: CertificateTranscriptParams,
	): Observable<Blob> {
		return this.http.get(
			`${this.certificatesUrl}/certificateTranscript/${id}`,
			{ params, responseType: 'blob' },
		);
	}*/

	downloadTranscript(
		id: number,
		params: CertificateTranscriptParams,
	): Observable<Blob> {
		return this.http
		.get<ApiResponse<CeCertificateFileResponse>>(
			`${this.settings.baseUrl}/api/certificateFile/certificate/${id}`,
			{ params },
			//{ params, responseType: 'blob' },
		)
		.pipe(
			catchError(() => of({statusMessage: { status: 'ERROR' }, response: {}} as ApiResponse<CeCertificateFileResponse>)),
			map((res: ApiResponse<CeCertificateFileResponse>) => this.b64toBlob(res?.response.encodedContent, res?.response.fileType)),
		);
	}

	// Legacy code
	/*private b64toBlob(b64Data, contentType = '', sliceSize = 512) {
		const byteCharacters = atob(b64Data);
		const byteArrays = [];

		for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
			const slice = byteCharacters.slice(offset, offset + sliceSize);

			const byteNumbers = Array(slice.length);
			for (let i = 0; i < slice.length; i++) {
				byteNumbers[i] = slice.charCodeAt(i);
			}

			const byteArray = new Uint8Array(byteNumbers);
			byteArrays.push(byteArray);
		}

		const blob = new Blob(byteArrays, { type: contentType });
		return blob;
	}*/


	fetchCertificate(id: string | number): Observable<CertificateResponse> {
		//console.log('fetchCertificate in certificates.api.service called with id:', id);
		const url = `${this.settings.baseUrl}/api/certificate/${id}`;

		return this.http
			.get<ApiResponse<CertificateResponse>>(url)
			.pipe(
				catchError(() => of({statusMessage: { status: 'ERROR' }, response: {}} as ApiResponse<CertificateResponse>)),
				map((res: ApiResponse<CertificateResponse>) => res?.response),
			);
	}

	/*fetchCertificateActions(id: number): Observable<CertificateAction[]> {
		const url = `${this.certificatesUrl}/certificateActions/${id}`;

		return this.http
			.get<CertificateActionResponse>(url)
			.pipe(
				catchError(() => of({ actions: [] } as CertificateActionResponse)),
				map((res: CertificateActionResponse) => res?.actions ?? []),
			);
	}*/

	fetchCertificateActions(id: number): Observable<CertificateAction[]> {
		const url = `${this.settings.baseUrl}/api/certificateActions/${id}`;

		return this.http
			.get<ApiResponse<CertificateActionResponse>>(url)
			.pipe(
				catchError(() => of({
						statusMessage: { status: 'ERROR' },
						response: { actions: [] },
					} as ApiResponse<CertificateActionResponse>)
				),
				map((res: ApiResponse<CertificateActionResponse>) => res?.response?.actions ?? []),
			);
	}
	
	
	getShortName(): Observable<ClassifierItemsQuery> {
		return this.classifiers.fetchClassifierItemsByDefinitionCode(
			ClassifierDefinitionCode.GRADUATION_DOCUMENT_TYPE,
		);
	}

	getCertificateDocumentsWithClassifiers(
		index: CertificateIndex,
		mainLanguage: GraduationDocumentLanguage,
	): Observable<CertificateDocumentWithClassifier[]> {
		const requests: Observable<CertificateDocumentWithClassifier>[] = [];
		index.documents.forEach((document) => {
			requests.push(
				this.classifiers
					.fetchClassifierItemByItemCodeWithParameters(document.type, {
						language: this.getLanguageCodeFromString(document.language),
					})
					.pipe(
						map((graduationType) => ({
							...document,
							metadata: graduationType,
							isMainDocument:
								CertificatesUtility.documentMatchesCertificateType(
									document,
									index,
								),
							isInMainLanguage: CertificatesUtility.documentMatchesMainLanguage(
								document,
								mainLanguage,
							),
						})),
					),
			);
		});
		return forkJoin(requests);
	}

	getQualificationFrameworks(
		key: string,
		language: string,
	): Observable<ClassifierItemsQueryItem> {
		return this.classifiers.fetchClassifierItemByItemCodeWithParameters(key, {
			language: this.getLanguageCodeFromString(language),
		});
	}

	getLanguageCodeFromString(languageString: string): string {
		return languageString ? languageString.split(':')[1] : null;
	}

	getDocumentsWithAllowedDisclosure(): Observable<ClassifierItemsQuery> {
		return this.classifiers.fetchClassifierItemsByDefinitionCodeWithParameters(
			ClassifierDefinitionCode.GRADUATION_DOCUMENT_TYPE,
			{
				attributeDefinitionCode:
					ClassifierAttributeDefinitionCode.GRADUATION_DOCUMENT_TYPE_DISCLOSURE_ALLOWED,
			},
		);
	}

	isDisclosureAllowed(
		document: GraduationDocumentType,
	): BehaviorSubject<boolean> {
		const isAllowed = new BehaviorSubject<boolean>(false);
		this.getDocumentsWithAllowedDisclosure().subscribe(
			(res: ClassifierItemsQuery) => {
				const items: ClassifierItemsQueryItem[] = res.classifierItems;
				const item = items
					.filter((el: ClassifierItemsQueryItem) =>
						el.attributes.find(
							(attribute: ClassifierAttribute) =>
								attribute.code ===
									GraduationDocumentAttribute.DISCLOSURE_ALLOWED &&
								attribute.value === '1',
						),
					)
					.find((el: ClassifierItemsQueryItem) => el.code === document);

				if (item) {
					const attribute: ClassifierAttribute = item.attributes.find(
						(el: ClassifierAttribute) =>
							el.code === GraduationDocumentAttribute.DISCLOSURE_ALLOWED,
					);
					isAllowed.next(!!parseInt(attribute.value, 10));
				}
			},
		);
		return isAllowed;
	}

	// Mimicks legacy isDisclosureAllowed but maps the fetch directly instead of changing an observable after it has returned
	getIsDisclosureAllowed(document: GraduationDocumentType) {
		return this.getDocumentsWithAllowedDisclosure().pipe(
			map((res: ClassifierItemsQuery) => {
				const items: ClassifierItemsQueryItem[] = res.classifierItems;
				const item = items
					.filter((el: ClassifierItemsQueryItem) =>
						el.attributes.find(
							(attribute: ClassifierAttribute) =>
								attribute.code ===
									GraduationDocumentAttribute.DISCLOSURE_ALLOWED &&
								attribute.value === '1',
						),
					)
					.find((el: ClassifierItemsQueryItem) => el.code === document);

				if (item) {
					const attribute: ClassifierAttribute = item.attributes.find(
						(el: ClassifierAttribute) =>
							el.code === GraduationDocumentAttribute.DISCLOSURE_ALLOWED,
					);
					return !!parseInt(attribute.value, 10);
				}
				return false;
			}),
		);
	}

	/*fetchCertificateWithAccess(accessType: AccessType) {
		//console.log('fetchCertificateWithAccess called with accessType:', accessType);
		return this.http.get(
			`${this.settings.ehisUrl}/certificates/v1/certificates?accessType=${accessType}`,
		);
	}*/

	fetchCertificateWithAccess(accessType: AccessType): Observable<CertificatesResponse> {
		return this.http.get<ApiResponse<CertificatesResponse>>(
			`${this.settings.baseUrl}/api/certificates?accessType=${accessType}`,
		)
		.pipe(
				catchError(() => {
					return of(
						{
							statusMessage: { status: 'ERROR' },
							response: { certificates: [] },
						} as ApiResponse<CertificatesResponse>,
					);
				}),
				map(
					(res: ApiResponse<CertificatesResponse>) =>
						res?.response,
				),
			);
		;
	}

	fetchCertificates(): Observable<GraduationCertificate[]> {
		//console.log('fetchCertificates in certificates.api.service called');
		return this.http
			.get<ApiResponse<CertificatesResponse>>(`${this.settings.baseUrl}/api/certificates`)
			.pipe(
				catchError(() => {
					return of(
						{
							statusMessage: { status: 'ERROR' },
							response: { certificates: [] },
						} as ApiResponse<CertificatesResponse>,
					);
				}),
				map(
					(res: ApiResponse<CertificatesResponse>) =>
						res?.response?.certificates ?? [],
				),
			);
	}

	/*fetchCertificates2() {
		return this.http
			.get<
				{ certificates: GraduationCertificate[] }
			>(`${this.settings.ehisUrl}/certificates/v1/certificates`)
			.pipe(
				catchError(() => {
					return of({ certificates: [] });
				}),
				map(
					(res: { certificates: GraduationCertificate[] }) =>
						res?.certificates ?? [],
				),
			);
	}*/

	fetchCertificateWithDocuments(id: number | string) {
		//console.log('fetchCertificateWithDocuments called with id:', id);
		return this.http
			.get<ApiResponse<CertificateData>>(`${this.settings.baseUrl}/api/certificate/${id}`)
			.pipe(
				switchMap((val: ApiResponse<CertificateData>) => {
					return this.getLatestDocuments(val.response);
				}),
			);
	}

	/*fetchCertificateWithDocuments(id: number | string) {
		console.log('fetchCertificateWithDocuments called with id:', id);
		return this.http
			.get<CertificateData>(`${this.settings.ehisUrl}/certificates/v1/certificate/${id}`)
			.pipe(
				switchMap((val: CertificateData) => {
					return this.getLatestDocuments(val);
				}),
			);
	}*/

	public getMatchingStudy(curriculumCode: string) {
		return this.http
			.get(
				`${this.settings?.url}/dashboard/eeIsikukaart/digital_sign_data?_format=json`,
			)
			.pipe(
				catchError(() => of({})),
				map((response?: DSVResponse) => {
					if (response?.value?.oping) {
						return (response?.value?.oping as Studies[])?.find((study) =>
							study?.oppekava?.some(
								(curriculum) => curriculum?.oppekavaKood === curriculumCode,
							),
						);
					}
					return undefined;
				}),
			);
	}

	public downloadSignedStudy(
		studyId: string | number,
		completionCallback?: (error?: unknown) => void,
	) {
		const body = {
			andmekirje: [studyId],
			andmeplokk: [],
			valjundiTyyp: [],
		};
		this.http
			.post(`${this.settings.url}/digi-signed`, body)
			.subscribe((response: any) => {
				if (response?.fileName && response?.value) {
					this.saveZip(response);
				}
				completionCallback(response?.error);
			});
	}

	public getCertificateActionsHistory(id: string | number) {
		return this.http
			.get<ApiResponse<CertificateActionResponse>>(`${this.settings.baseUrl}/api/certificateActions/${id}`)
			.pipe(
				catchError(() => of({
						statusMessage: { status: 'ERROR' },
						response: { actions: [] },
					} as ApiResponse<CertificateActionResponse>)
				),
				map((res: ApiResponse<CertificateActionResponse>) =>
					((res?.response?.actions ?? []) as (CertificateAction & { issueTime: number })[])?.toSorted((a, b) => {
						if (new Date(a.added) > new Date(b.added)) {
							return -1;
						}
						if (new Date(a.issueTime) < new Date(b.added)) {
							return 1;
						}
						return 0;
					}),
				),
			);
	}

	/*public getCertificateActionsHistory(id: string | number) {
		return this.http
			.get(`${this.settings.ehisUrl}/certificates/v1/certificateActions/${id}`)
			.pipe(
				catchError(() => of({})),
				map(
					(res: { actions?: (CertificateAction & { issueTime: number })[] }) =>
						(res?.actions ?? [])?.toSorted((a, b) => {
							if (new Date(a.added) > new Date(b.added)) {
								return -1;
							}
							if (new Date(a.issueTime) < new Date(b.added)) {
								return 1;
							}
							return 0;
						}),
				),
			);
	}*/

	public getCertificateIssues(id: string | number) {
		return this.http
			.get<ApiResponse<CertificateDataIssueResponse>>(
				//`${this.settings.ehisUrl}/certificates/v1/certificateDataIssues/${id}`,
				`${this.settings.baseUrl}/api/certificateDataIssues/${id}`,
			)
			.pipe(
				catchError(() => of({
						statusMessage: { status: 'ERROR' },
						response: { dataIssues: [] },
					} as ApiResponse<CertificateDataIssueResponse>)

				),
				map((res: ApiResponse<CertificateDataIssueResponse>) =>
					res?.response?.dataIssues
						?.filter((el) => el.issueBase !== 'OWNER')
						?.toSorted((a, b) => {
							if (new Date(a.issueTime) > new Date(b.issueTime)) {
								return -1;
							}
							if (new Date(a.issueTime) < new Date(b.issueTime)) {
								return 1;
							}
							return 0;
						}),
				),
			);
	}

	/*public getCertificateIssues(id: string | number) {
		return this.http
			.get<CertificateDataIssue[]>(
				`${this.settings.ehisUrl}/certificates/v1/certificateDataIssues/${id}`,
			)
			.pipe(
				catchError(() => of([] as CertificateDataIssue[])),
				map((res: CertificateDataIssue[]) =>
					res
						?.filter((el) => el.issueBase !== 'OWNER')
						?.toSorted((a, b) => {
							if (new Date(a.issueTime) > new Date(b.issueTime)) {
								return -1;
							}
							if (new Date(a.issueTime) < new Date(b.issueTime)) {
								return 1;
							}
							return 0;
						}),
				),
			);
	}*/

	public getValidCertificateAccesses(id: string | number): Observable<CertificateAccess[]> {
		console.log('getValidCertificateAccesses called with id:', id);
		return this.http.get<ApiResponse<CertificateAccessResponse>>(
			//`${this.settings.baseUrl}/api/certificateAccess?indexId=${id}&status=ACCESS_STATUS:VALID`,
			`${this.settings.baseUrl}/api/certificateAccess/${id}?status=ACCESS_STATUS:VALID`,
		)
		.pipe(
			map((resp) => {
				if (resp?.statusMessage?.status === 'ERROR') {
					const isAccessForbidden = resp.statusMessage.message?.includes('certificateAccessNotFound') ?? false;
					const error = new Error(resp.statusMessage.message ?? 'Unknown error') as Error & { isAccessForbidden: boolean };
					error.isAccessForbidden = isAccessForbidden;
					throw error;
				}
				return resp?.response?.certificateAccesses ?? [];
			})
		)
		;
	}

	public getInvalidCertificateAccesses(id: string | number): Observable<CertificateAccess[]> {
		console.log('getInvalidCertificateAccesses called with id:', id);
		return this.http
			.get<ApiResponse<CertificateAccessResponse>>(
				//>(`${this.settings.ehisUrl}/certificates/v1/certificateAccess?indexId=${id}&status=ACCESS_STATUS:INVALID`)
				`${this.settings.baseUrl}/api/certificateAccess/${id}?status=ACCESS_STATUS:INVALID`,
			)
			.pipe(
				catchError(() => 
					of({
						statusMessage: { status: 'ERROR' },
						response: { certificateAccesses: [] },
					} as ApiResponse<CertificateAccessResponse>)
				),
				map((resp) =>
					((resp?.response?.certificateAccesses) ?? []).toSorted((a, b) => {
						if ((a.issued ?? '') > (b.issued ?? '')) {
							return -1;
						}
						if ((a.issued ?? '') < (b.issued ?? '')) {
							return 1;
						}
						return 0;
					}),
				),
			)
			;
	}

	public addAccess(data: CertificateAccessDTO) {
		return this.http
			//.post(`${this.settings.ehisUrl}/certificates/v1/certificateAccess`, data)
			.post(`${this.settings.baseUrl}/api/certificateAccess`, data)
			.pipe(
				catchError((err) => {
					const message = err?.error?.errors?.[0]?.message;
					if (message) {
						this.alertsService.error(message, 'global', 'access-errors', true);
					}
					return of({});
				}),
			);
	}

	public editAccess(data: CertificateEditAccessDto) {
		return this.http
			//.patch(`${this.settings.ehisUrl}/certificates/v1/certificateAccess`, data)
			.patch(`${this.settings.baseUrl}/api/certificateAccess`, data)
			.pipe(
				catchError((err) => {
					const message = err?.error?.errors?.[0]?.message;
					if (message) {
						this.alertsService.error(message, 'global', 'access-errors', true);
					}
					return of({});
				}),
			);
	}

	public invalidateAccess(certificateId: string | number, accessId: number) {
		return this.http
			//.delete(`${this.settings.ehisUrl}/certificates/v1/certificateAccess?indexId=${certificateId}&accessId=${accessId}`,);
			.delete(`${this.settings.baseUrl}/api/certificateAccess?indexId=${certificateId}&accessId=${accessId}`);
	}

	private getLatestDocuments(data: CertificateData) {
		const { documents } = data.index;
		const documentRequests: Observable<CertificateDocumentResponse>[] = [];
		let result: FormattedCertificateDocumentData & {
			documents?: CertificateDocumentWithClassifier[];
			index?: CertificateIndex;
			typeTitle?: string;
		};
		if (data?.index?.documents?.length > 0) {
			result = CertificatesUtility.getLatestDocumentData(documents);
			if (result?.certificate?.id) {
				// Request certificate itself by ID
				documentRequests.push(this.fetchDocument(result?.certificate?.id));
				if (result?.certificateEN?.id) {
					documentRequests.push(this.fetchDocument(result?.certificateEN?.id));
				}
				// Request transcript by ID
				if (result?.transcript?.id) {
					documentRequests.push(this.fetchDocument(result?.transcript?.id));
				}
				// Request supplement by ID
				if (result?.supplement?.id) {
					documentRequests.push(this.fetchDocument(result?.supplement?.id));
				}

				if (result?.transcriptEN?.id) {
					documentRequests.push(this.fetchDocument(result?.transcriptEN?.id));
				}
				if (result?.supplementEN?.id) {
					documentRequests.push(this.fetchDocument(result?.supplementEN?.id));
				}
			}
		} else {
			return of({ index: data.index } as FormattedCertificateDocumentData & {
				documents?: CertificateDocumentWithClassifier[];
				index?: CertificateIndex;
				typeTitle?: string;
			});
		}

		return forkJoin(documentRequests).pipe(
			map((docs: CertificateDocumentResponse[]) => {
				// Clean up certificate, supplement and transcript from response
				result = CertificatesUtility.parseSupplementaryDocuments(
					result,
					docs,
					data,
				);
				result.index = data?.index;
				// Reduce the amount of document queries
				result.index.documents = result?.index?.documents?.filter(
					(document) => document?.status === CertificateStatus.VALID,
				);
				return result;
			}),
			switchMap((val) => {
				return this.getCertificateDocumentsWithClassifiers(
					data.index,
					GraduationDocumentLanguage.emakeelEt,
				).pipe(
					map(
						(documentsWithClassifiers: CertificateDocumentWithClassifier[]) => {
							val.documents = CertificatesUtility.getValidDocuments(
								documentsWithClassifiers,
							);
							val.typeTitle = CertificatesUtility.typeTitle(
								result.certificate,
								documentsWithClassifiers,
							);
							return val;
						},
					),
				);
			}),
		);
	}

	private saveZip(file: any) {
		const blob = this.b64toBlob(file.value, 'application/vnd.etsi.asic-e+zip');
		const elem = globalThis?.window?.document?.createElement('a');
		elem.href = globalThis?.window?.URL?.createObjectURL(blob);
		elem.download = file?.fileName;
		globalThis?.document?.body?.appendChild(elem);
		elem.click();
		globalThis?.document?.body?.removeChild(elem);
	}

	// Legacy code
	private b64toBlob(b64Data, contentType = '', sliceSize = 512) {
		const byteCharacters = atob(b64Data);
		const byteArrays = [];

		for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
			const slice = byteCharacters.slice(offset, offset + sliceSize);

			const byteNumbers = Array(slice.length);
			for (let i = 0; i < slice.length; i++) {
				byteNumbers[i] = slice.charCodeAt(i);
			}

			const byteArray = new Uint8Array(byteNumbers);
			byteArrays.push(byteArray);
		}

		const blob = new Blob(byteArrays, { type: contentType });
		return blob;
	}
}
